import { assert } from '@ember/debug';
import type { Column } from 'slickgrid';
import { userEntryToNumber } from '@ebiz/pretty-num';
import Validation from './validation.ts';
import type { ColumnFilter } from '../components/aditools-device-table.ts';
import type { DeviceTableDeviceData } from '@ebiz/aditools-databook';

const _stripTrailingDecimal = function (item: string) {
  const dotted = item.split('.');
  return dotted.length === 2 && dotted[1] && dotted[1].length === 0 ? item.slice(0, -1) : item;
};

export default class PriceFilter implements ColumnFilter<DeviceTableDeviceData> {
  defaultRelation: string;
  relation: string;
  initialValue: string;
  value: string;
  qaName?: string;

  constructor(defaultRelation: string, initialValue?: string, qaName?: string) {
    this.defaultRelation = defaultRelation ?? 'GT';
    this.relation = this.defaultRelation;
    this.initialValue = initialValue ?? '';
    this.value = this.initialValue;
    this.qaName = qaName;
  }

  populate(column: Column<DeviceTableDeviceData>): HTMLElement {
    if (!column) {
      throw 'column passed to populate is invalid';
    }
    const selection = this.generateDOMElement(column);
    const selectionElement = selection.children[0] as HTMLSelectElement;
    const inputElement = selection.children[1] as HTMLInputElement;
    selectionElement.value = this.relation;
    inputElement.value = this.value;
    assert('At least one element is always generated', selection);
    return selection;
  }

  generateDOMElement(column: Column<DeviceTableDeviceData>): HTMLDivElement {
    const selectQaElement = this.qaName ? `data-qa-element="device_table_${this.qaName}_select"` : '';
    const inputQaElement = this.qaName ? `data-qa-element="device_table_${this.qaName}_input"` : '';
    const divElement = document.createElement('div');
    divElement.className = 'slickgrid-filter-engineering';
    divElement.innerHTML = `
      <select id="filter-${column.id}-rel" class="slickgrid-filter-relation" aria-label="${column.name} filter direction" ${selectQaElement} data-column-id="${column.id}">
        <option value="EQ">=</option>
      </select>
      <input id="filter-${column.id}-val" class="slickgrid-filter-relative-value" type="text" autocomplete="off" aria-label="${column.name} filter value" ${inputQaElement} data-column-id="${column.id}" />`;
    return divElement;
  }

  update(element: HTMLInputElement): void {
    const children = element.parentElement?.children;
    if (children) {
      this.relation = (<HTMLInputElement>children[0]).value;
      this.value = (<HTMLInputElement>children[1]).value;
    }
  }

  unpack(packedValues: string): void {
    if (packedValues) {
      const initialValues = JSON.parse(packedValues);
      if (
        Object.prototype.hasOwnProperty.call(initialValues, 'relation') &&
        Object.prototype.hasOwnProperty.call(initialValues, 'value')
      ) {
        this.relation = initialValues.relation;
        this.value = initialValues.value;
      }
    }
  }

  pack(): string {
    return JSON.stringify({ relation: this.relation, value: this.value });
  }

  hasValue(): boolean {
    return this.value !== '';
  }

  reset(): void {
    this.relation = this.defaultRelation;
    this.value = this.initialValue;
  }

  filter(field: string): boolean {
    const fieldValue1 = field && field.split(' ~ ')[0] ? userEntryToNumber(field.split(' ~ ')[0] ?? '') : field;
    const fieldValue2 = field && field.split(' ~ ')[1] ? userEntryToNumber(field.split(' ~ ')[1] ?? '') : field;
    const filterRelation = this.relation;
    const filterValueString = _stripTrailingDecimal(this.value);
    if (Validation.validateFieldInput(filterValueString, 'ENGINEERING')) {
      const filterValue = userEntryToNumber(filterValueString);
      if (filterValue && fieldValue1 && fieldValue2) {
        switch (filterRelation) {
          case 'EQ':
            return filterValue >= (fieldValue1 as number) && filterValue <= (fieldValue2 as number);
        }
      }
    }
    return true;
  }
}
